import { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const navigate = useNavigate();
  const [pageError, setPageError] = useState(null);

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setToken(null);
    navigate("/login");
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return; // 🚀 Don't navigate immediately, let the app handle it!
    }

    try {
      const decoded = jwtDecode(token);
      const isExpired = decoded.exp * 1000 < Date.now();

      if (isExpired) {
        toast.error("Your session has expired. Please login again.");
        logout(); // 🔥 Call logout to handle token removal & navigation properly
      } else {
        setUser(decoded);
      }
    } catch (error) {
      toast.error("Invalid session. Please login again.");
      logout(); // 🔥 Call logout to clean up token properly
    } finally {
      setLoading(false);
    }
  }, [token]); // ✅ Removed navigate from dependencies

  const login = async (email, password) => {
    try {
      const response = await axios.post("https://new.cajuu.app/api/login", {
        email,
        password,
      });

      const newToken = response.data.data.token;
      localStorage.setItem("token", newToken);
      setUser(jwtDecode(newToken));
      setToken(newToken);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.details?.error || "invalid login credentials",
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, login, logout, loading, setPageError, pageError }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
